import React from 'react';
import { useLocation } from 'react-router-dom';
import { reportError } from '../utils/errorReporting';
import { isChunkLoadError, reloadOnceForChunkError } from '../utils/chunkLoadError';

// Catches render errors inside the routed page only. The outer ErrorBoundary
// in index.js replaces the whole app with a full-screen message; this one
// keeps the navigation, banners and modals mounted so the member can move to
// another page or reload, and it resets itself on every route change.
class PageErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, reloading: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  // Clear a crashed page when the member navigates elsewhere. Done here rather
  // than with key={pathname}: a key change remounts every route, so the
  // /overview → /overview/<id> single-plan redirect wiped Overview's state and
  // closed the GetLaterModal a `?action=delay` magic link had just opened.
  componentDidUpdate(prevProps) {
    if (this.state.hasError && prevProps.path !== this.props.path) {
      this.setState({ hasError: false, reloading: false });
    }
  }

  componentDidCatch(error, errorInfo) {
    if (isChunkLoadError(error)) {
      if (reloadOnceForChunkError()) {
        this.setState({ reloading: true });
        return;
      }
      reportError(error, { source: 'chunk_load_error', componentStack: errorInfo?.componentStack });
      return;
    }
    reportError(error, {
      source: 'page_error_boundary',
      path: this.props.path,
      componentStack: errorInfo?.componentStack,
    });
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.state.reloading) return null;
    return (
      <div className="flex items-center justify-center px-4 py-16">
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm p-8 text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">This page hit a snag</h2>
          <p className="text-gray-600 mb-6">
            Sorry about that — something on this page didn&apos;t load correctly. Reloading usually fixes it.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex items-center justify-center px-6 py-3 rounded-full bg-gray-900 text-white font-medium hover:bg-gray-700 transition-colors"
            >
              Reload page
            </button>
            <a
              href="/overview"
              className="inline-flex items-center justify-center px-6 py-3 rounded-full border border-gray-300 text-gray-900 font-medium hover:bg-gray-100 transition-colors"
            >
              Back to overview
            </a>
          </div>
        </div>
      </div>
    );
  }
}

// Not keyed by pathname (see componentDidUpdate): /overview and /overview/<id>
// must keep one Overview instance across the redirect between them.
const RouteErrorBoundary = ({ children }) => {
  const location = useLocation();
  return (
    <PageErrorBoundary path={location.pathname}>
      {children}
    </PageErrorBoundary>
  );
};

export { PageErrorBoundary };
export default RouteErrorBoundary;
